import { mat3, mat4, vec2, vec3, vec4 } from "gl-matrix";
import { Animation } from "../../Common/Animation/Animation";
import { AnimationState } from "../Animation/AnimationState";
import { PropertyAnimation } from "../../Common/Animation/PropertyAnimation";

import { Context } from "../Engine";
import { toRadian } from "@spi/Common/Math/math";
import { BoundingBox } from "@spi/Common/Util";
import { Mesh, MeshLayoutSegment } from "../Mesh/Mesh";
import { IDrawableObject, RenderSettings, RenderMode, ObjectInteraction } from "./ObjectManager";
import { CompositeShaderSettings, CompositeShader } from "../Shader";
import { ITexture } from "../Texture";
import { Camera } from "../View/Camera";

// TODO: split RenderObject in Animated/Static Objects

export class RenderObject implements IDrawableObject {

    public animatedProperties: PropertyAnimation;

    public interaction: ObjectInteraction;

    public mesh: Mesh;

    public position: vec3;
    public fixedRotation: number;
    public rotation: number;
    public rotationAxis: vec3;
    public scale: vec3;
    public baseColor: vec4;
    public texture: ITexture;

    public dynamicLight: boolean;

    public animationState: AnimationState;

    public mvMatrix: mat4;
    public bindShapeMatrix: mat4;
    public boneMatrix: mat4;
    public offsetMatrix: mat4;

    public attachments: IAttachmentHolder;
    public attachmentIdentity: Attachment;

    public get order(): number {
        if (this.texture && this.texture.translucent) {
            return 2000;
        } else {
            return 1000;
        }
    }

    public get animation(): Animation {
        return this.mesh.animation;
    }

    constructor(mesh: Mesh) {
        this.mesh = mesh;

        this.texture = mesh.texture;
        this.baseColor = vec4.fromValues(1, 1, 1, 1);

        this.position = vec3.fromValues(0, 0, 0);
        this.rotation = 0;
        this.fixedRotation = 0;
        this.rotationAxis = vec3.fromValues(0, 0, 1);
        this.setScale(1);

        this.animationState = null;

        this.dynamicLight = false;
    }

    public get isAnimated(): boolean {
        return !!(this.animation && this.animation.validAnimation && this.animationState);
    }

    public setScale(scale: number) {
        this.scale = vec3.fromValues(scale, scale, scale);
    }

    public rotateTo(direction: vec3 | vec2): void {
        let rotRad = Math.atan2(direction[0], direction[1]);

        let rotDeg = rotRad * 180 / Math.PI;
        if (rotDeg < 0) {
            // Create range of 0 to 360 degrees instead of -180 to 180
            rotDeg = 360 - Math.abs(rotDeg);
        }
        this.rotation = -rotDeg + this.fixedRotation;
    }

    public getPosition() {
        return this.position;
    }

    public getBoundingBox(): BoundingBox {
        // TODO: get actual size of object instead of 2x2x2
        let min = vec3.subtract(vec3.create(), this.position, [2, 2, 2]);
        let max = vec3.add(vec3.create(), this.position, [2, 2, 2]);

        return new BoundingBox(min, max);
    }

    private getShaderSettings(mode: RenderMode): CompositeShaderSettings {
        let settings: CompositeShaderSettings = [];

        if (this.isAnimated) {
            settings.push('ANIMATION');
        }

        if (this.attachmentIdentity) {
            settings.push('ATTACHMENT');
        }

        if (mode == RenderMode.Picking) {
            settings.push('PICKING');
        } if (mode == RenderMode.Primary) {
            if (this.dynamicLight) {
                settings.push('DYNAMIC_LIGHTNING');
            }

            if (this.mesh.dataLayout.staticLight) {
                settings.push('ATTRIBUTE_LIGHT');
            }

            if (this.texture) {
                settings.push('TEXTURE');
            } else {
                settings.push('ATTRIBUTE_COLOR', 'PRESET_COLOR');
            }
        } else if (mode == RenderMode.Occlusion) {
            if (this.dynamicLight) {
                settings.push('DYNAMIC_LIGHTNING');
            }

            if (this.mesh.dataLayout.staticLight) {
                settings.push('ATTRIBUTE_LIGHT');
            }

            settings.push('PRESET_COLOR');
        }

        return settings;
    }

    public render(ctx: Context, camera: Camera, settings: RenderSettings) {

        let shader = ctx.engine.shaderManager.getCompositeShader(...this.getShaderSettings(settings.mode));
        ctx.setShader(shader);

        const gl = ctx.gl;

        let modelViewMatrix: mat4 = null;

        if (this.attachmentIdentity) {
            // Object is attached to some other object.
            modelViewMatrix = this.attachmentIdentity.modelViewMatrix;
        } else {
            modelViewMatrix = mat4.create();
            mat4.copy(modelViewMatrix, camera.modelViewMatrix);

            mat4.translate(modelViewMatrix, modelViewMatrix, this.position);
            mat4.scale(modelViewMatrix, modelViewMatrix, this.scale);
            if (this.rotation !== 0) {
                mat4.rotate(modelViewMatrix, modelViewMatrix, toRadian(this.rotation), this.rotationAxis);
            }
        }

        if (this.attachments) {
            this.attachments.preRenderAttachments(modelViewMatrix);
        }

        gl.uniformMatrix4fv(shader.uniforms.uProjectionMatrix, false, camera.projectionMatrix);
        gl.uniformMatrix4fv(shader.uniforms.uModelViewMatrix, false, modelViewMatrix);

        let vertexBuffer = this.mesh.buffer;

        gl.bindBuffer(gl.ARRAY_BUFFER, vertexBuffer.glVertexBuffer);

        this.bindAttributeFloat32(ctx, shader.attributes.aVertexPosition, this.mesh.dataLayout.position);

        switch (settings.mode) {
            case RenderMode.Occlusion:
                this.setupOcclusionRender(ctx, shader, camera, modelViewMatrix);
                break;
            case RenderMode.Primary:
                this.setupPrimaryRender(ctx, shader, camera, modelViewMatrix);
                break;
            case RenderMode.Picking:
                this.setupPickingRender(ctx, shader, modelViewMatrix, settings.pickingReference);
                break;
        }

        gl.drawArrays(vertexBuffer.glDrawMode, 0, vertexBuffer.itemCount);
    }

    private setupPickingRender(ctx: Context, shader: CompositeShader, modelViewMatrix: mat4, pickingReference: number) {
        const gl = ctx.gl;

        gl.uniform4fv(shader.uniforms.uPickingColor, vec4.fromValues((pickingReference & 0xFF) / 255, (pickingReference >> 8 & 0xFF) / 255, (pickingReference >> 16 & 0xFF) / 255, 1));

        if (this.isAnimated) {
            this.renderAnimationDataTexture(ctx, shader);
        }

        if (this.attachmentIdentity) {
            gl.uniformMatrix4fv(shader.uniforms.uBoneMatrix, false, this.attachmentIdentity.boneMatrix);
            gl.uniformMatrix4fv(shader.uniforms.uOffsetMatrix, false, this.attachmentIdentity.offsetMatrix);
        }
    }

    private setupOcclusionRender(ctx: Context, shader: CompositeShader, camera: Camera, modelViewMatrix: mat4) {
        const gl = ctx.gl;

        this.bindAttributeFloat32(ctx, shader.attributes.aVertexNormal, this.mesh.dataLayout.normal);

        gl.uniform4fv(shader.uniforms.uPresetColor, [0, 0, 0, 1]);

        if (this.dynamicLight) {
            this.renderLightning(ctx, shader, camera, modelViewMatrix);
        }

        if (this.isAnimated) {
            this.renderAnimationDataTexture(ctx, shader);
        }

        if (this.mesh.dataLayout.staticLight) {
            this.bindAttributeFloat32(ctx, shader.attributes.aVertexLight, this.mesh.dataLayout.staticLight);
        }

        if (this.attachmentIdentity) {
            gl.uniformMatrix4fv(shader.uniforms.uBoneMatrix, false, this.attachmentIdentity.boneMatrix);
            gl.uniformMatrix4fv(shader.uniforms.uOffsetMatrix, false, this.attachmentIdentity.offsetMatrix);
        }
    }

    private setupPrimaryRender(ctx: Context, shader: CompositeShader, camera: Camera, modelViewMatrix: mat4) {
        const gl = ctx.gl;

        this.bindAttributeFloat32(ctx, shader.attributes.aVertexNormal, this.mesh.dataLayout.normal);

        if (this.texture) {
            this.setupTexturedVertices(ctx, shader);
        } else {
            this.setupColoredVertices(ctx, shader);
        }

        if (this.dynamicLight) {
            this.renderLightning(ctx, shader, camera, modelViewMatrix);
        }

        if (this.isAnimated) {
            this.renderAnimationDataTexture(ctx, shader);
        }

        if (this.attachmentIdentity) {
            gl.uniformMatrix4fv(shader.uniforms.uBoneMatrix, false, this.attachmentIdentity.boneMatrix);
            gl.uniformMatrix4fv(shader.uniforms.uOffsetMatrix, false, this.attachmentIdentity.offsetMatrix);
        }
    }

    private renderLightning(ctx: Context, shader: CompositeShader, camera: Camera, modelViewMatrix: mat4) {
        let gl = ctx.gl;

        let worldInverseMatrix = mat4.invert(mat4.create(), modelViewMatrix);
        let worldInverseTransposeMatrix = mat4.transpose(mat4.create(), worldInverseMatrix);

        //let normalMatrix = mat3.normalFromMat4(mat3.create(), modelViewMatrix);
        gl.uniformMatrix4fv(shader.uniforms.uModelViewInverseTransposeMatrix, false, worldInverseTransposeMatrix);
        gl.uniformMatrix4fv(shader.uniforms.uViewMatrix, false, camera.modelViewMatrix);

        let lightManager = ctx.engine.activeScene.lightManager;

        gl.uniform3fv(shader.uniforms.uCameraWorldPosition, camera.position);
        gl.uniform3fv(shader.uniforms.uAmbientColor, lightManager.ambientColor);
        gl.uniform1f(shader.uniforms.uShininess, 0.5);
        //gl.uniformMatrix4fv(shader.uniforms.uLightMatrix, false, modelViewMatrix);

        let lights = lightManager.getNearestLights(this.position);
        let lightCount = Math.min(8, lights.length);
        for (let i = 0; i < lightCount; i++) {
            gl.uniform4fv(shader.uniforms.uPointLightingColor[i], lights[i].color);
            gl.uniform4fv(shader.uniforms.uPointLightingLocation[i], lights[i].position);
        }
        
        gl.uniform1i(shader.uniforms.uLightCount, lightCount);
    }

    private renderAnimationDataTexture(ctx: Context, shader: CompositeShader) {
        let gl = ctx.gl;

        this.bindAttributeFloat32(ctx, shader.attributes.aJointWeights, this.mesh.dataLayout.jointWeights);
        this.bindAttributeFloat32(ctx, shader.attributes.aJointIndices, this.mesh.dataLayout.jointIndices);

        // The bind shape matrix aligns the vertices to the right position to apply bone transformations to them
        gl.uniformMatrix4fv(shader.uniforms.uBindShapeMatrix, false, this.animation.bindShapeMatrix);
        gl.uniformMatrix4fv(shader.uniforms.uInvBindShapeMatrix, false, this.animation.invBindShapeMatrix);

        let frameSet = this.animationState.getFrames();

        let offset = this.mesh.animationDataTexture.getFrameOffset(frameSet.state, frameSet.frameIndex);

        let dimension = this.mesh.animationDataTexture.dimension;
        gl.uniform3fv(shader.uniforms.uDataSettings, [dimension.width, dimension.height, offset]);

        gl.activeTexture(gl.TEXTURE0);
        gl.bindTexture(gl.TEXTURE_2D, this.mesh.animationDataTexture.glTexture);
        gl.uniform1i(shader.uniforms.uMatrixSampler, 0); // bind texture0
    }

    private setupColoredVertices(ctx: Context, shader: CompositeShader) {
        let gl = ctx.gl;

        if (!this.mesh.dataLayout.color) {
            return;
        }

        this.bindAttributeFloat32(ctx, shader.attributes.aVertexColor, this.mesh.dataLayout.color);

        gl.uniform4fv(shader.uniforms.uPresetColor, this.baseColor);
    }

    private setupTexturedVertices(ctx: Context, shader: CompositeShader) {
        let gl = ctx.gl;

        if (!this.mesh.dataLayout.diffuseTexture) {
            return;
        }

        this.bindAttributeFloat32(ctx, shader.attributes.aTextureCoord, this.mesh.dataLayout.diffuseTexture);

        if (this.mesh.dataLayout.staticLight) {
            this.bindAttributeFloat32(ctx, shader.attributes.aVertexLight, this.mesh.dataLayout.staticLight);
        }

        gl.uniform4fv(shader.uniforms.uPresetColor, vec4.fromValues(1, 1, 1, 1));

        gl.activeTexture(gl.TEXTURE1);
        gl.bindTexture(gl.TEXTURE_2D, this.texture.glTexture);
        gl.uniform1i(shader.uniforms.uTextureSampler, 1); // bind texture1
    }

    private bindAttributeFloat32(ctx: Context, attribute: number, segment: MeshLayoutSegment) {
        let gl = ctx.gl;
        gl.vertexAttribPointer(attribute, segment.length, gl.FLOAT, false, Float32Array.BYTES_PER_ELEMENT * this.mesh.buffer.itemSize, Float32Array.BYTES_PER_ELEMENT * segment.offset);
    }

}

export interface IAttachmentHolder {

    setAttachment(attachPoint: string, obj: RenderObject): Attachment;
    getAttachment(attachPoint: string): Attachment;
    clearAttachment(attachPoint: string): Attachment;

    preRenderAttachments(modelViewMatrix: mat4): void;
}

export class Attachment {
    modelViewMatrix: mat4;
    offsetMatrix: mat4;
    boneMatrix: mat4;

    constructor(public attachPoint: string, public object: RenderObject, public parent: RenderObject) {
        this.modelViewMatrix = mat4.create();
        this.offsetMatrix = mat4.create();
        this.boneMatrix = mat4.create();
    }
}